import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import {
  OptionText,
  ToggleContainer,
  ToggleOption,
  Highlight,
  ToggleTrack
} from './styled';

interface ToggleProps<T extends string> {
  options: { id: T; name: string }[];
  selectedValue: T;
  cypress?: string;
  onChange: (value: T) => void;
  stat?: string;
  allowReselect?: boolean;
}

export function Toggle<T extends string>({
  options,
  selectedValue,
  cypress,
  stat,
  allowReselect = false,
  onChange
}: ToggleProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [highlightStyle, setHighlightStyle] = useState<{
    left: number;
    right: number;
  } | null>(null);

  const currentIndex = options.findIndex(o => o.id === selectedValue);

  const recompute = () => {
    const el = optionRefs.current[currentIndex];
    const track = trackRef.current;

    if (!el || !track) return;

    setHighlightStyle({
      left: el.offsetLeft,
      right: track.offsetWidth - (el.offsetLeft + el.offsetWidth)
    });
  };

  useLayoutEffect(() => {
    recompute();
  }, [currentIndex, options]);

  useEffect(() => {
    const observer = new ResizeObserver(recompute);
    if (trackRef.current) observer.observe(trackRef.current);
    optionRefs.current.forEach(opt => opt && observer.observe(opt));

    return () => observer.disconnect();
  }, []);

  const goNext = () => {
    const nextIndex = (currentIndex + 1) % options.length;
    onChange(options[nextIndex].id);
  };

  const handleOptionClick = (optionId: T, isSelected: boolean) => {
    if (isSelected) {
      if (allowReselect) goNext();
    } else {
      onChange(optionId);
    }
  };

  const highlightClipPath = highlightStyle
    ? `inset(0 ${highlightStyle.right}px 0 ${highlightStyle.left}px round 30px)`
    : 'inset(0 100% 0 0 round 30px)';

  const highlightInlineStyle = {
    clipPath: highlightClipPath,
    WebkitClipPath: highlightClipPath
  };

  return (
    <ToggleContainer data-cy={cypress}>
      <ToggleTrack ref={trackRef}>
        <Highlight style={highlightInlineStyle} />
        {options.map((option, i) => {
          const isSelected = option.id === selectedValue;
          const optionCypress = `${cypress}${isSelected ? '-active' : ''}-option`;

          return (
            <ToggleOption
              key={option.id}
              ref={el => {
                optionRefs.current[i] = el;
              }}
              $isSelected={isSelected}
              onClick={() => handleOptionClick(option.id, isSelected)}
              data-cy={optionCypress}
              data-stat={stat}
              $allowReselect={allowReselect}
            >
              <OptionText tag="body_1" $isSelected={isSelected}>
                {option.name}
              </OptionText>
            </ToggleOption>
          );
        })}
      </ToggleTrack>
    </ToggleContainer>
  );
}
